import swirlBottomLeft from '@assets/images/common/swirl-bottom-left.svg';
import swirlTopRight from '@assets/images/common/swirl-top-right.svg';
import { CobaltLink } from '@cae/cobalt-react/components/link';
import { CobaltLogo } from '@cae/cobalt-react/components/logo';
import { CobaltIconHelpOutline } from '@cae/cobalt-react/icons/outline/CobaltIconHelpOutline';
import { FeedbackContent } from '@shared/components/feedback-content/FeedbackContent';
import { FEEDBACK_CONFIGS } from '@shared/components/feedback-content/FeedbackContent.utils';
import { navigateToHelp } from '@shared/helpers/navigate-to-help/navigateToHelp';
import * as React from 'react';

import type { AuthFeedbackPageProps } from './AuthFeedbackPage.types';

import styles from './AuthFeedbackPage.module.css';

const AuthFeedbackPage = React.memo(
  ({
    type,
    primaryActionLabel,
    onPrimaryAction,
    secondaryActionLabel,
    onSecondaryAction,
  }: Readonly<AuthFeedbackPageProps>): React.ReactElement => {
    const headingRef = React.useRef<HTMLHeadingElement>(null);
    const config = FEEDBACK_CONFIGS[type];

    React.useEffect(() => {
      headingRef.current?.focus();
    }, []);

    return (
      <div className={styles.authFeedbackPage} data-testid={`${type}-page`}>
        <img
          src={swirlTopRight}
          alt=""
          aria-hidden="true"
          className={styles.swirlTopRight}
        />
        <img
          src={swirlBottomLeft}
          alt=""
          aria-hidden="true"
          className={styles.swirlBottomLeft}
        />
        <header className={styles.header} data-testid={`${type}-header`}>
          <CobaltLogo size="medium" color="white" />
          <CobaltLink
            variant="inverse"
            onClick={navigateToHelp}
            className={styles.helpButton}
          >
            <CobaltIconHelpOutline aria-hidden="true" />
            Help
          </CobaltLink>
        </header>
        <h1
          className={`${styles.appTitle} heading-05`}
          data-testid={`${type}-app-title`}
        >
          CAE Connect
        </h1>
        <div className={styles.content}>
          <FeedbackContent
            title={config.title}
            titleRef={headingRef}
            message={config.message}
            illustration={<config.Illustration />}
            primaryActionLabel={primaryActionLabel}
            onPrimaryAction={onPrimaryAction}
            secondaryActionLabel={secondaryActionLabel}
            onSecondaryAction={onSecondaryAction}
          />
        </div>
        <footer
          className={`${styles.footer} body-01`}
          data-testid={`${type}-footer`}
        >
          &copy;{new Date().getFullYear()}, CAE Inc. All rights reserved.
        </footer>
      </div>
    );
  }
);

AuthFeedbackPage.displayName = 'AuthFeedbackPage';

export { AuthFeedbackPage };
